export const content = {
  en: {
    langName: "English",
    nav: { about: "About", approach: "Approach", model: "Model", capabilities: "Capabilities", contact: "Contact" },
    hero: {
      kicker: "Independent upstream investment & development",
      titleLines: ["Unlocking value", "where experience", "*makes the difference.*"],
      intro:
        "OSRELIS invests in and develops upstream oil & gas opportunities where operational judgement, capital discipline and pragmatic execution can transform complex assets into durable industrial value.",
      cta1: "Discover our approach",
      cta2: "Discuss an opportunity",
      index: [
        { l1: "Upstream", l2: "Oil & Gas" },
        { l1: "Africa & selected", l2: "international markets" },
        { l1: "Investment · Development", l2: "Industrial partnership" },
      ],
      scroll: "Scroll",
    },
    about: {
      label: "About OSRELIS",
      title: "An investment platform\nwith an *operator's mindset.*",
      lead: "OSRELIS is an independent energy investment and development company headquartered in Dubai.",
      p2: "We focus on upstream opportunities where technical complexity, mature infrastructure or non-standard project economics require a different approach: experienced, entrepreneurial and relentlessly focused on execution.",
      p3: "Our model combines selective capital deployment with hands-on industrial involvement, from early asset evaluation to development strategy and operational readiness.",
      link: "Explore our capabilities",
      principles: [
        { t: "Selective", d: "We pursue opportunities where our experience can materially change the outcome." },
        { t: "Pragmatic", d: "Fit-for-purpose solutions, disciplined scope and rapid decision-making." },
        { t: "Aligned", d: "Partnership structures designed around shared value creation over the long term." },
      ],
      caption: "Marginal & mature assets",
      leadership: {
        label: "Leadership",
        name: "Benoit de la Fouchardiere",
        role: "Founder & CEO — OSRELIS, Dubai",
        statement:
          "A senior oil & gas executive and former CEO of Perenco, Benoit has led exploration, development, production and decommissioning programmes across multiple regions — founding OSRELIS to focus on the disciplined redevelopment of marginal fields.",
      },
    },
    approach: {
      label: "Our Approach",
      title: "Complex fields do not need\ncomplex organisations.",
      body: "They need experienced people, clear accountability and development concepts designed around the asset — not the other way around.",
      quote: "“Audacity creates opportunity.\nExperience makes it executable.\n*Discipline makes it sustainable.”*",
    },
    marquee: ["Upstream", "Investment", "Development", "Discipline", "Execution", "Partnership"],
    capabilities: {
      label: "Capabilities",
      title: "From opportunity screening\nto *first production.*",
      intro:
        "OSRELIS brings an integrated view across technical, commercial and execution disciplines — with a particular focus on lean redevelopment of marginal and mature assets.",
      items: [
        { t: "Asset Evaluation", d: "Rapid technical, commercial and strategic screening. Development potential, risks, infrastructure constraints and value drivers." },
        { t: "Development Strategy", d: "Fit-for-purpose concepts balancing recovery, capex, schedule, operability and long-term field economics." },
        { t: "Project Execution", d: "Contracting strategy, refurbishment and conversion, yards, logistics, offshore installation and operational readiness." },
        { t: "Investment Structuring", d: "Equity, farm-in, co-investment and tailored industrial partnership models aligned with project requirements." },
      ],
    },
    model: {
      label: "Development Model",
      imgA: "First oil",
      imgB: "Storage & offloading",
      imgC: "Offshore operations",
      title: "A disciplined,\n*phased* development model.",
      intro:
        "Marginal fields do not tolerate the classic operator playbook of exhaustive studies and heavy front-end cost. OSRELIS sequences value in two phases — a lean first phase engineered to pay for its own facilities, followed by full-field development once production confirms the concept.",
      phase1Label: "Phase I — First oil",
      phase2Label: "Phase II — Full field (~12 months on)",
      phase1: [
        { t: "Leverage existing data", d: "Rely on prior studies and focus effort on the key technical questions — activation, sand control and deliverability." },
        { t: "Twin & up-dip wells", d: "Drill a twin of the discovery well, then an up-dip well targeting the best remaining potential." },
        { t: "MOPU & minimal facilities", d: "Install a Mobile Offshore Production Unit with fit-for-purpose surface facilities — nothing more." },
        { t: "Pipeline & early production", d: "Lay a line to shore or a floating terminal and produce three-phase fluids. Phase I is designed to pay for the facilities." },
      ],
      phase2: [
        { t: "Full field development", d: "Engage a complete Plan of Development — typically four to eight wells — once early production confirms the model." },
        { t: "Near-field exploration", d: "Pursue adjacent prospects, preferably on a different reservoir or risk profile, to balance the portfolio." },
      ],
      metrics: [
        { unit: "MMbbl STOIIP", note: "Target field size — oil initially in place" },
        { unit: "MMbbl recoverable", note: "Alternative screening criterion" },
        { unit: "Water depth", note: "Onshore through to offshore" },
        { unit: "bopd", note: "Threshold to begin trading production" },
      ],
    },
    opportunities: {
      label: "Selected Opportunities",
      title: "Africa remains at the core\nof our *investment thesis.*",
      p1: "OSRELIS evaluates selected upstream opportunities across Africa, with particular interest in producing, marginal and redevelopment assets where existing infrastructure and disciplined capital can accelerate value creation.",
      p2: "We engage with asset owners, operators, governments, financial partners and specialist contractors under appropriate confidentiality arrangements.",
      tags: ["Marginal fields", "Mature assets", "Redevelopment", "Near-field opportunities", "Operator partnerships"],
      confTag: "Private by design",
      confText: "Project names, transaction terms and partner information are not disclosed publicly.",
    },
    partnership: {
      label: "Partnership",
      imgCaption: "Industrial milestones, shared value",
      title: "Capital alone is not\nthe differentiator.\n*Alignment is.*",
      lead: "We build partnerships around clear responsibilities, transparent economics and fast decision-making.",
      p2: "OSRELIS can participate as investor, co-developer or strategic industrial partner, adapting its level of involvement to the opportunity and the capabilities of each party.",
      partners: ["Asset Owners", "Operators", "Co-Investors", "Governments", "Industrial Partners"],
    },
    visualBand: { lines: ["Where discipline meets", "*the horizon.*"] },
    gallery: { kicker: "On site", title: "From the *field.*" },
    faq: {
      label: "Questions",
      title: "Clarity before\n*commitment.*",
      items: [
        { q: "What kind of assets does OSRELIS target?", a: "Marginal, mature and redevelopment upstream oil & gas assets — typically under 20 MMbbl of oil initially in place, or under 5 MMbbl of recoverable reserves — from onshore through to offshore water depths of up to 120 metres." },
        { q: "How does OSRELIS reduce development risk on marginal fields?", a: "Through a disciplined, phased model. A lean Phase I is engineered to pay for its own facilities using existing data, twin and up-dip wells and a MOPU. Full-field development follows only once early production confirms the concept." },
        { q: "Where does OSRELIS invest?", a: "Africa remains at the core of our investment thesis, alongside selected international markets, with a focus on producing, marginal and redevelopment assets." },
        { q: "How can partners engage with OSRELIS?", a: "We participate as investor, co-developer or strategic industrial partner, adapting our involvement to the opportunity — engaging asset owners, operators, governments and contractors under appropriate confidentiality arrangements." },
        { q: "Are project details disclosed publicly?", a: "No. Project names, transaction terms and partner information remain private by design and are only shared under confidentiality." },
      ],
    },
    contact: {
      label: "Contact",
      title: "Have an upstream\n*opportunity to discuss?*",
      intro: "For investment opportunities, partnerships or strategic discussions, contact OSRELIS directly.",
      hqPrefix: "Headquartered in",
      hqPlace: "Dubai, United Arab Emirates",
      form: {
        name: "Full name",
        email: "Email",
        company: "Company (optional)",
        message: "Message",
        submit: "Send message",
        sending: "Sending…",
        success: "Message sent — OSRELIS will be in touch.",
        error: "Something went wrong. Please try again.",
      },
    },
    footer: {
      copyright: "© 2026 OSRELIS. Independent Upstream Energy Investment & Development.",
      rights: "All rights reserved.",
    },
  },

  fr: {
    langName: "Français",
    nav: { about: "À propos", approach: "Approche", model: "Modèle", capabilities: "Expertises", contact: "Contact" },
    hero: {
      kicker: "Investissement & développement upstream indépendant",
      titleLines: ["Révéler la valeur", "là où l'expérience", "*fait la différence.*"],
      intro:
        "OSRELIS investit dans et développe des opportunités pétrolières et gazières upstream où le jugement opérationnel, la discipline financière et une exécution pragmatique transforment des actifs complexes en valeur industrielle durable.",
      cta1: "Découvrir notre approche",
      cta2: "Échanger sur une opportunité",
      index: [
        { l1: "Upstream", l2: "Pétrole & Gaz" },
        { l1: "Afrique & marchés", l2: "internationaux ciblés" },
        { l1: "Investissement · Développement", l2: "Partenariat industriel" },
      ],
      scroll: "Défiler",
    },
    about: {
      label: "À propos d'OSRELIS",
      title: "Une plateforme d'investissement\navec l'*état d'esprit d'un opérateur.*",
      lead: "OSRELIS est une société indépendante d'investissement et de développement énergétique, basée à Dubaï.",
      p2: "Nous ciblons les opportunités upstream où la complexité technique, des infrastructures matures ou une économie de projet atypique exigent une approche différente : expérimentée, entrepreneuriale et résolument tournée vers l'exécution.",
      p3: "Notre modèle associe un déploiement de capital sélectif à une implication industrielle concrète, de l'évaluation initiale des actifs à la stratégie de développement et à la préparation opérationnelle.",
      link: "Explorer nos expertises",
      principles: [
        { t: "Sélectif", d: "Nous poursuivons les opportunités où notre expérience peut réellement changer l'issue." },
        { t: "Pragmatique", d: "Des solutions adaptées, un périmètre maîtrisé et des décisions rapides." },
        { t: "Aligné", d: "Des partenariats conçus autour d'une création de valeur partagée sur le long terme." },
      ],
      caption: "Actifs marginaux & matures",
      leadership: {
        label: "Direction",
        name: "Benoit de la Fouchardiere",
        role: "Fondateur & CEO — OSRELIS, Dubaï",
        statement:
          "Dirigeant pétrolier et gazier chevronné et ancien CEO de Perenco, Benoit a piloté des programmes d'exploration, de développement, de production et de démantèlement dans plusieurs régions — fondant OSRELIS pour se concentrer sur le redéveloppement discipliné des champs marginaux.",
      },
    },
    approach: {
      label: "Notre approche",
      title: "Les champs complexes n'exigent pas\ndes organisations complexes.",
      body: "Ils exigent des personnes expérimentées, des responsabilités claires et des concepts de développement pensés autour de l'actif — et non l'inverse.",
      quote: "« L'audace crée l'opportunité.\nL'expérience la rend exécutable.\n*La discipline la rend durable. »*",
    },
    marquee: ["Upstream", "Investissement", "Développement", "Discipline", "Exécution", "Partenariat"],
    capabilities: {
      label: "Expertises",
      title: "Du filtrage des opportunités\nà la *première production.*",
      intro:
        "OSRELIS apporte une vision intégrée des disciplines techniques, commerciales et d'exécution — avec un accent particulier sur le redéveloppement optimisé des actifs marginaux et matures.",
      items: [
        { t: "Évaluation d'actifs", d: "Filtrage technique, commercial et stratégique rapide. Potentiel de développement, risques, contraintes d'infrastructure et leviers de valeur." },
        { t: "Stratégie de développement", d: "Des concepts adaptés équilibrant récupération, capex, calendrier, exploitabilité et économie du champ à long terme." },
        { t: "Exécution de projet", d: "Stratégie contractuelle, rénovation et conversion, chantiers, logistique, installation offshore et préparation opérationnelle." },
        { t: "Structuration d'investissement", d: "Prise de participation, farm-in, co-investissement et modèles de partenariat industriel sur-mesure, alignés sur les besoins du projet." },
      ],
    },
    model: {
      label: "Modèle de développement",
      imgA: "First oil",
      imgB: "Stockage & évacuation",
      imgC: "Opérations offshore",
      title: "Un modèle de développement\ndiscipliné et *par phases.*",
      intro:
        "Les champs marginaux ne tolèrent pas le schéma classique des opérateurs, fait d'études exhaustives et de coûts initiaux lourds. OSRELIS séquence la valeur en deux phases — une première phase allégée, conçue pour financer ses propres installations, suivie d'un développement complet du champ une fois le concept confirmé par la production.",
      phase1Label: "Phase I — Premier pétrole",
      phase2Label: "Phase II — Champ complet (~12 mois plus tard)",
      phase1: [
        { t: "Exploiter les données existantes", d: "S'appuyer sur les études antérieures et concentrer l'effort sur les questions techniques clés — activation, contrôle des sables et débit." },
        { t: "Puits jumeau & up-dip", d: "Forer un jumeau du puits de découverte, puis un puits up-dip visant le meilleur potentiel restant." },
        { t: "MOPU & installations minimales", d: "Installer une unité de production offshore mobile (MOPU) avec des installations de surface adaptées — rien de plus." },
        { t: "Pipeline & production précoce", d: "Poser une conduite vers la côte ou un terminal flottant et produire des fluides triphasiques. La Phase I est conçue pour financer les installations." },
      ],
      phase2: [
        { t: "Développement complet du champ", d: "Engager un plan de développement complet — généralement de quatre à huit puits — une fois le modèle confirmé par la production précoce." },
        { t: "Exploration de proximité", d: "Poursuivre des prospects adjacents, de préférence sur un réservoir ou un profil de risque différent, pour équilibrer le portefeuille." },
      ],
      metrics: [
        { unit: "MMbbl STOIIP", note: "Taille cible du champ — pétrole initialement en place" },
        { unit: "MMbbl récupérables", note: "Critère de filtrage alternatif" },
        { unit: "Profondeur d'eau", note: "De l'onshore à l'offshore" },
        { unit: "bopd", note: "Seuil pour commencer à commercialiser la production" },
      ],
    },
    opportunities: {
      label: "Opportunités sélectionnées",
      title: "L'Afrique reste au cœur\nde notre *thèse d'investissement.*",
      p1: "OSRELIS évalue des opportunités upstream ciblées à travers l'Afrique, avec un intérêt particulier pour les actifs en production, marginaux et de redéveloppement où des infrastructures existantes et un capital discipliné peuvent accélérer la création de valeur.",
      p2: "Nous dialoguons avec les propriétaires d'actifs, les opérateurs, les gouvernements, les partenaires financiers et les contractants spécialisés, dans le cadre d'accords de confidentialité appropriés.",
      tags: ["Champs marginaux", "Actifs matures", "Redéveloppement", "Opportunités de proximité", "Partenariats opérateurs"],
      confTag: "Confidentiel par nature",
      confText: "Les noms de projets, les conditions de transaction et les informations sur les partenaires ne sont pas divulgués publiquement.",
    },
    partnership: {
      label: "Partenariat",
      imgCaption: "Jalons industriels, valeur partagée",
      title: "Le capital seul n'est pas\nle facteur différenciant.\n*L'alignement l'est.*",
      lead: "Nous construisons des partenariats autour de responsabilités claires, d'une économie transparente et de décisions rapides.",
      p2: "OSRELIS peut intervenir comme investisseur, co-développeur ou partenaire industriel stratégique, adaptant son niveau d'implication à l'opportunité et aux capacités de chaque partie.",
      partners: ["Propriétaires d'actifs", "Opérateurs", "Co-investisseurs", "Gouvernements", "Partenaires industriels"],
    },
    visualBand: { lines: ["Là où la discipline rejoint", "*l'horizon.*"] },
    gallery: { kicker: "Sur le terrain", title: "Depuis le *terrain.*" },
    faq: {
      label: "Questions",
      title: "La clarté avant\nl'*engagement.*",
      items: [
        { q: "Quels types d'actifs OSRELIS cible-t-elle ?", a: "Des actifs upstream pétroliers et gaziers marginaux, matures et de redéveloppement — généralement moins de 20 MMbbl de pétrole initialement en place, ou moins de 5 MMbbl de réserves récupérables — de l'onshore jusqu'à des profondeurs d'eau offshore atteignant 120 mètres." },
        { q: "Comment OSRELIS réduit-elle le risque de développement sur les champs marginaux ?", a: "Grâce à un modèle discipliné et par phases. Une Phase I allégée est conçue pour financer ses propres installations en s'appuyant sur les données existantes, des puits jumeaux et up-dip et une MOPU. Le développement complet du champ ne suit qu'une fois le concept confirmé par la production précoce." },
        { q: "Où OSRELIS investit-elle ?", a: "L'Afrique reste au cœur de notre thèse d'investissement, aux côtés de marchés internationaux ciblés, avec un accent sur les actifs en production, marginaux et de redéveloppement." },
        { q: "Comment les partenaires peuvent-ils collaborer avec OSRELIS ?", a: "Nous intervenons comme investisseur, co-développeur ou partenaire industriel stratégique, en adaptant notre implication à l'opportunité — en dialoguant avec les propriétaires d'actifs, les opérateurs, les gouvernements et les contractants dans le cadre d'accords de confidentialité appropriés." },
        { q: "Les détails des projets sont-ils divulgués publiquement ?", a: "Non. Les noms de projets, les conditions de transaction et les informations sur les partenaires restent confidentiels par nature et ne sont partagés que sous confidentialité." },
      ],
    },
    contact: {
      label: "Contact",
      title: "Vous avez une opportunité\nupstream à *discuter ?*",
      intro: "Pour des opportunités d'investissement, des partenariats ou des échanges stratégiques, contactez OSRELIS directement.",
      hqPrefix: "Siège social à",
      hqPlace: "Dubaï, Émirats Arabes Unis",
      form: {
        name: "Nom complet",
        email: "E-mail",
        company: "Société (facultatif)",
        message: "Message",
        submit: "Envoyer le message",
        sending: "Envoi…",
        success: "Message envoyé — OSRELIS vous recontactera.",
        error: "Une erreur est survenue. Veuillez réessayer.",
      },
    },
    footer: {
      copyright: "© 2026 OSRELIS. Investissement & développement énergétique upstream indépendant.",
      rights: "Tous droits réservés.",
    },
  },
};
